import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment'
import { Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import useFetch from '../../hooks/fetchMSSQL-hook';
import { NavigationContext } from '../../context/navigation-context';
import NoResults from '../../components/NoResults/NoResults';
import { NotificationManager } from 'react-notifications';
import SearchFilter from '../../components/Filter/SearchFilter';
import { API_URL } from '../../config/config';

import {
  ACCEPTED_EXTENSIONS,
  MAX_FILE_SIZE_MB,
  MAX_VIDEO_FILES,
  MAX_TOTAL_FILES,
  DEFAULT_NON_N62_RESOLUTION,
  getExtension,
  bytesToMb,
  parseMaybeJSON,
  toBool,
  normalizeNewAdvertiseFile,
  normalizeExistingAdvertiseFile,
  getAdvertiseType,
  buildAdvertiseFormData,
} from './AdvertisesUploadService';

const STATUS_META = {
  NotPublished: { label: 'Nieopublikowane', className: 'badge badge-secondary' },
  ToPublish: { label: 'Do publikacji', className: 'badge badge-warning' },
  Published: { label: 'Opublikowane', className: 'badge badge-success' },
};

const SQUARE_RESOLUTION = '480x480';

const TERMINAL_TYPE_LABELS = {
  P: 'PAX',
  N: 'NAYAX',
  A: 'Newland',
  X: 'NEXGO',
  V: 'Vendotek',
  1: 'Vendotek',
  2: 'PAX',
  3: 'NAYAX',
  4: 'Newland',
  5: 'NEXGO',
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'bmp'];

const sortDefaults = {
  Name: 'ASC',
  Comment: 'ASC',
  PackageType: 'ASC',
  FilesCount: 'DESC',
  ScreenRatioKey: 'ASC',
  TerminalsCount: 'DESC',
  StartDelaySec: 'ASC',
  ImageDurationSec: 'ASC',
  Priority: 'DESC',
  PublishStatus: 'ASC',
  UpdateDateTime: 'DESC',
  IsActive: 'DESC',
};

const createEmptyAdvertise = () => ({
  AdvertiseId: null,
  Name: '',
  Priority: 1,
  StartDelaySec: 15,
  ImageDurationSec: 30,
  MoveSensitivity: 0,
  ShowMessage: true,
  IsActive: true,
  PublishStatus: 'NotPublished',
  Comment: '',
  TerminalIds: [],
  TerminalSerials: [],
  FilesMeta: [],
  PackageType: '',
  ScreenRatioKey: '',
  CreateDateTime: null,
  CreateUserId: null,
  UpdateDateTime: null,
  UpdateUserId: null,
});

const normalizeApiMediaPath = (value) => String(value || '').replace(/\\/g, '/').replace(/^\/+/, '');

const buildApiMediaUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^blob:/i.test(raw) || /^data:/i.test(raw) || /^https?:\/\//i.test(raw)) {
    return raw;
  }

  return `${API_URL}/${normalizeApiMediaPath(raw)}`;
};

const formatDate = (value) => {
  if (!value) return '-';

  const m = moment(value);
  return m.isValid() ? m.format('YYYY.MM.DD HH:mm') : String(value);
};

const normalizePublishStatus = (status) => {
  const value = String(status || '').trim();

  if (value === 'NotPublished' || value === 'Nieopublikowane') return 'NotPublished';
  if (value === 'ToPublish' || value === 'Do publikacji') return 'ToPublish';
  if (value === 'Published' || value === 'Opublikowane') return 'Published';

  return 'NotPublished';
};

const isAcceptedExtension = (ext) => {
  const normalizedExt = String(ext || '').replace('.', '').toLowerCase();
  const normalizedWithDot = `.${normalizedExt}`;

  if (!normalizedExt) return false;

  if (ACCEPTED_EXTENSIONS?.has) {
    return ACCEPTED_EXTENSIONS.has(normalizedExt) || ACCEPTED_EXTENSIONS.has(normalizedWithDot);
  }

  if (Array.isArray(ACCEPTED_EXTENSIONS)) {
    return (
      ACCEPTED_EXTENSIONS.includes(normalizedExt) ||
      ACCEPTED_EXTENSIONS.includes(normalizedWithDot)
    );
  }

  return ['mp4', 'jpg', 'jpeg', 'png', 'bmp'].includes(normalizedExt);
};

const getTerminalTypeName = (terminal) => {
  const directName = String(terminal?.TerminalTypeName || '').trim();
  if (directName) return directName;

  const rawType = String(terminal?.TerminalType || '').trim();
  return TERMINAL_TYPE_LABELS[rawType] || rawType || 'Terminal';
};

const getScreenDataFromTerminal = (terminal) => {
  const explicitKey = String(terminal?.ScreenKey || terminal?.ScreenRatioKey || '').trim();
  const explicitResolution = String(terminal?.Resolution || terminal?.ScreenResolution || '').trim();

  if (explicitKey) {
    return {
      key: explicitKey,
      label: explicitKey,
      resolution:
        explicitResolution ||
        (explicitKey === '1:1' ? SQUARE_RESOLUTION : DEFAULT_NON_N62_RESOLUTION),
      isMixed: false,
    };
  }

  const model = String(terminal?.Model || terminal?.ModelName || '').trim().toUpperCase();

  if (model.includes('N62')) {
    return {
      key: '1:1',
      label: '1:1',
      resolution: SQUARE_RESOLUTION,
      isMixed: false,
    };
  }

  return {
    key: '9:16',
    label: '9:16',
    resolution: DEFAULT_NON_N62_RESOLUTION,
    isMixed: false,
  };
};

const normalizeTerminalFromApi = (terminal) => {
  const terminalId = Number(terminal?.TerminalId);
  if (!Number.isFinite(terminalId)) return null;

  const terminalTypeName = getTerminalTypeName(terminal);
  const model = String(terminal?.Model || terminal?.ModelName || '').trim();
  const serialNo = String(
    terminal?.SerialNo ||
      terminal?.SerialNumber ||
      terminal?.TerminalSerialNo ||
      terminal?.serialNo ||
      terminal?.serialNumber ||
      ''
  ).trim();

  const screen = getScreenDataFromTerminal(terminal);

  const terminalName =
    [terminalTypeName, model, serialNo].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim() ||
    String(terminal?.Name || '').trim() ||
    `Terminal ${terminalId}`;

  return {
    TerminalId: terminalId,
    SerialNo: serialNo,
    TerminalType: terminal?.TerminalType || '',
    TerminalTypeName: terminalTypeName,
    Model: model,
    TerminalName: terminalName,
    ScreenKey: screen.key,
    Resolution: screen.resolution,
    RatioLabel: screen.label,
    CustomerName: terminal?.CustomerName || '',
    MachineFullName: terminal?.MachineFullName || '',
    IsFallback: false,
  };
};

const createFallbackTerminal = (terminalId) => {
  const screen = getScreenDataFromTerminal({});

  return {
    TerminalId: Number(terminalId) || 0,
    SerialNo: '',
    TerminalType: '',
    TerminalTypeName: 'Terminal',
    Model: '',
    TerminalName: `Terminal ${terminalId}`,
    ScreenKey: screen.key,
    Resolution: screen.resolution,
    RatioLabel: screen.label,
    CustomerName: '',
    MachineFullName: '',
    IsFallback: true,
  };
};

const makeExistingFileKey = (file, index) => {
  const id =
    file?.AdvertiseFileId ||
    file?.FileId ||
    file?.id ||
    file?.StoredFileName ||
    file?.OriginalFileName ||
    file?.name ||
    index;

  return `existing-${id}-${index}`;
};

const normalizeExistingAdvertiseFileSafe = (file, index) => {
  const normalized = normalizeExistingAdvertiseFile(file, index) || {};

  const originalFileName =
    normalized.OriginalFileName ||
    normalized.name ||
    file?.OriginalFileName ||
    file?.name ||
    file?.FileName ||
    file?.StoredFileName ||
    '';

  const storedFileName =
    normalized.StoredFileName ||
    file?.StoredFileName ||
    file?.StoredName ||
    file?.FileName ||
    originalFileName;

  const ext = String(
    normalized.ext ||
      normalized.FileExt ||
      file?.FileExt ||
      file?.ext ||
      getExtension(originalFileName || storedFileName)
  )
    .replace('.', '')
    .toLowerCase();

  const size = Number(normalized.size ?? normalized.FileSize ?? file?.FileSize ?? file?.size ?? 0);

  const width = Number(
    normalized.Width ?? normalized.width ?? file?.Width ?? file?.width ?? file?.ImageWidth ?? 0
  );

  const height = Number(
    normalized.Height ??
      normalized.height ??
      file?.Height ??
      file?.height ??
      file?.ImageHeight ??
      0
  );

  const hasDimensions =
    Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;

  return {
    ...normalized,
    key: normalized.key || makeExistingFileKey(file, index),

    existing: true,
    isExisting: true,
    IsExisting: true,

    file: null,
    rawFile: null,
    sourceFile: null,

    AdvertiseFileId: normalized.AdvertiseFileId || file?.AdvertiseFileId || null,
    FileId: normalized.FileId || file?.FileId || null,

    name: normalized.name || originalFileName || storedFileName || `plik-${index + 1}`,
    OriginalFileName: originalFileName || storedFileName || `plik-${index + 1}`,
    StoredFileName: storedFileName || originalFileName || `plik-${index + 1}`,
    FilePath: normalized.FilePath || file?.FilePath || file?.Path || '',
    FileExt: ext,
    ext,
    MimeType: normalized.MimeType || file?.MimeType || file?.mimeType || '',
    FileSize: size,
    size,
    SortOrder: Number(normalized.SortOrder ?? file?.SortOrder ?? index + 1),
    IsActive: normalized.IsActive ?? file?.IsActive ?? 1,

    ...(hasDimensions
      ? {
          Width: width,
          Height: height,
          width,
          height,
          Resolution: `${width}x${height}`,
        }
      : {}),
  };
};

const normalizeNewAdvertiseFileSafe = (file, index) => {
  const normalized = normalizeNewAdvertiseFile(file, index) || {};

  return {
    ...normalized,
    key:
      normalized.key ||
      `new-${file?.name || 'file'}-${file?.size || 0}-${file?.lastModified || Date.now()}-${index}`,
    existing: false,
    isExisting: false,
    IsExisting: false,
    file: normalized.file || file,
    rawFile: normalized.rawFile || file,
    sourceFile: normalized.sourceFile || file,
  };
};

const parseResolution = (resolution) => {
  const match = String(resolution || '')
    .trim()
    .match(/(\d+)\s*[x×]\s*(\d+)/i);

  if (!match) return null;

  const width = Number(match[1]);
  const height = Number(match[2]);

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  return { width, height };
};

const getReducedRatioLabel = (width, height) => {
  const a = Math.abs(Number(width));
  const b = Math.abs(Number(height));

  if (!Number.isFinite(a) || !Number.isFinite(b) || !a || !b) return '-';

  const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
  const divisor = gcd(a, b);

  return `${a / divisor}:${b / divisor}`;
};

const getRatioKeyFromDimensions = (width, height) => {
  const w = Number(width);
  const h = Number(height);

  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return '';

  const ratio = w / h;

  if (Math.abs(ratio - 1) <= 0.02) return '1:1';
  if (Math.abs(ratio - 9 / 16) <= 0.02) return '9:16';

  return getReducedRatioLabel(w, h);
};

const getFileDimensions = (file) => {
  const width = Number(
    file?.Width ??
      file?.width ??
      file?.ImageWidth ??
      file?.VideoWidth ??
      file?.MediaWidth ??
      0
  );

  const height = Number(
    file?.Height ??
      file?.height ??
      file?.ImageHeight ??
      file?.VideoHeight ??
      file?.MediaHeight ??
      0
  );

  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { width, height };
  }

  return parseResolution(file?.Resolution || file?.resolution);
};

const readImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;

      URL.revokeObjectURL(url);

      if (width && height) {
        resolve({ width, height });
      } else {
        reject(new Error('Nie udało się odczytać rozdzielczości obrazu'));
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Nie udało się odczytać rozdzielczości obrazu'));
    };

    image.src = url;
  });

const readVideoDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    video.preload = 'metadata';
    video.muted = true;

    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;

      URL.revokeObjectURL(url);

      if (width && height) {
        resolve({ width, height });
      } else {
        reject(new Error('Nie udało się odczytać rozdzielczości filmu'));
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Nie udało się odczytać rozdzielczości filmu'));
    };

    video.src = url;
    video.load();
  });

const readMediaDimensions = async (file) => {
  const ext = String(getExtension(file?.name || '') || '')
    .replace('.', '')
    .toLowerCase();

  if (ext === 'mp4') {
    return readVideoDimensions(file);
  }

  if (IMAGE_EXTENSIONS.includes(ext)) {
    return readImageDimensions(file);
  }

  return null;
};

const normalizeNewAdvertiseFileWithDimensions = async (file, index) => {
  const normalized = normalizeNewAdvertiseFileSafe(file, index);

  try {
    const dimensions = await readMediaDimensions(file);

    if (!dimensions) return normalized;

    const resolution = `${dimensions.width}x${dimensions.height}`;

    return {
      ...normalized,
      Width: dimensions.width,
      Height: dimensions.height,
      width: dimensions.width,
      height: dimensions.height,
      Resolution: resolution,
      RatioKey: getRatioKeyFromDimensions(dimensions.width, dimensions.height),
    };
  } catch (err) {
    return {
      ...normalized,
      DimensionError: err?.message || 'Nie udało się odczytać rozdzielczości pliku',
    };
  }
};

const getFileScreenValidationError = (file, screen) => {
  if (!file) return null;

  const fileName = file.name || file.OriginalFileName || '-';

  if (file.DimensionError) {
    return `${fileName}: ${file.DimensionError}`;
  }

  if (!screen || screen.key === 'NONE' || screen.key === 'MIXED') {
    return null;
  }

  const dimensions = getFileDimensions(file);

  if (!dimensions) {
    return null;
  }

  const expectedResolution = parseResolution(screen.resolution);

  if (
    expectedResolution &&
    (dimensions.width !== expectedResolution.width || dimensions.height !== expectedResolution.height)
  ) {
    return `Wybrany plik ${fileName} ma rozdzielczość ${dimensions.width}x${dimensions.height}, a wybrany terminal wymaga ${expectedResolution.width}x${expectedResolution.height}`;
  }

  const fileRatioKey = getRatioKeyFromDimensions(dimensions.width, dimensions.height);

  if (fileRatioKey && fileRatioKey !== screen.key) {
    return `Wybrany plik ${fileName} ma proporcje ${fileRatioKey}, a wybrany terminal wymaga ${screen.key}`;
  }

  return null;
};

const hasUsableSourceFile = (file) => !!(file?.file || file?.rawFile || file?.sourceFile);

const isExistingAdvertiseFileMeta = (file) => {
  if (!file) return false;

  if (file.existing === true || file.isExisting === true || file.IsExisting === true) return true;

  const stringFlags = [file.existing, file.isExisting, file.IsExisting].map((value) =>
    String(value || '').toLowerCase()
  );

  if (stringFlags.includes('true') || stringFlags.includes('1')) return true;

  return !!(
    file.AdvertiseFileId ||
    file.FileId ||
    file.FilePath ||
    file.StoredFileName ||
    file.Path
  );
};

const normalizeFileForSubmit = (file, index) => {
  const existing = isExistingAdvertiseFileMeta(file);
  const sourceFile = file?.file || file?.rawFile || file?.sourceFile || null;

  return {
    ...file,
    existing,
    isExisting: existing,
    IsExisting: existing,
    file: existing ? null : sourceFile,
    rawFile: existing ? null : sourceFile,
    sourceFile: existing ? null : sourceFile,
    SortOrder: index + 1,
  };
};

const hasPublishTargets = (draft) => {
  const serials = Array.isArray(draft?.TerminalSerials) ? draft.TerminalSerials : [];

  return serials.some((terminal) => {
    if (typeof terminal === 'string' || typeof terminal === 'number') {
      return String(terminal || '').trim();
    }

    return String(
      terminal?.SerialNo ||
        terminal?.serialNo ||
        terminal?.SerialNumber ||
        terminal?.serialNumber ||
        terminal?.TerminalSerialNo ||
        terminal?.terminalSerialNo ||
        ''
    ).trim();
  });
};

const getAdvertiseValidation = (
  draft,
  getScreenSummary,
  { allowInactive = true, requirePublishTargets = false } = {}
) => {
  const errors = {};
  const files = draft?.FilesMeta || [];
  const terminals = draft?.TerminalIds || [];
  const screen = getScreenSummary(terminals);
  const type = getAdvertiseType(files);

  if (!String(draft?.Name || '').trim()) {
    errors.Name = 'Nazwa zestawu jest wymagana';
  }

  if (!terminals.length) {
    errors.TerminalIds = 'Wybierz co najmniej jeden terminal';
  }

  if (!files.length) {
    errors.FilesMeta = terminals.length
      ? 'Dodaj co najmniej jeden plik'
      : 'Najpierw wybierz terminal';
  }

  if (!allowInactive && !toBool(draft?.IsActive)) {
    errors.IsActive = 'Nie można przekazać do publikacji lub opublikować nieaktywnego zestawu';
  }

  if (requirePublishTargets && !hasPublishTargets(draft)) {
    errors.TerminalIds =
      'Brak numerów seryjnych terminali do przekazania reklam na SFTP. Odśwież listę terminali albo sprawdź, czy terminale mają SerialNo';
  }

  if (screen.isMixed) {
    errors.TerminalIds =
      'Wybrane terminale mają różne proporcje ekranu. Zrób osobny zestaw dla każdego typu';
  }

  if (files.length > MAX_TOTAL_FILES && !errors.FilesMeta) {
    errors.FilesMeta = `Maksymalnie ${MAX_TOTAL_FILES} plików w zestawie`;
  }

  files.forEach((file) => {
    const ext = String(file.ext || file.FileExt || '').toLowerCase();
    const existing = isExistingAdvertiseFileMeta(file);
    const hasSource = hasUsableSourceFile(file);

    if (!isAcceptedExtension(ext) && !errors.FilesMeta) {
      errors.FilesMeta = `Plik ${file.name || file.OriginalFileName || '-'} ma niedozwolone rozszerzenie`;
    }

    if (
      Number(file.size ?? file.FileSize ?? 0) > MAX_FILE_SIZE_MB * 1024 * 1024 &&
      !errors.FilesMeta
    ) {
      errors.FilesMeta = `Plik ${file.name || file.OriginalFileName || '-'} przekracza ${MAX_FILE_SIZE_MB} MB`;
    }

    if (!existing && !hasSource && !errors.FilesMeta) {
      errors.FilesMeta = `Nie znaleziono pliku źródłowego dla: ${
        file.name || file.OriginalFileName || '-'
      }. Usuń ten plik z listy i wybierz go ponownie`;
    }

    const screenValidationError = getFileScreenValidationError(file, screen);

    if (screenValidationError && !errors.FilesMeta) {
      errors.FilesMeta = screenValidationError;
    }
  });

  const hasVideo = files.some(
    (file) => String(file.ext || file.FileExt || '').toLowerCase() === 'mp4'
  );

  const hasImage = files.some((file) =>
    IMAGE_EXTENSIONS.includes(String(file.ext || file.FileExt || '').toLowerCase())
  );

  if (hasVideo && hasImage && !errors.FilesMeta) {
    errors.FilesMeta = 'Nie mieszaj MP4 ze zdjęciami w jednym zestawie';
  }

  if (type === 'Video' && files.length > MAX_VIDEO_FILES && !errors.FilesMeta) {
    errors.FilesMeta = `Dla MP4 maksymalnie ${MAX_VIDEO_FILES} plików`;
  }

  const imageDuration = Number(draft?.ImageDurationSec);
  const moveSensitivity = Number(draft?.MoveSensitivity);
  const startDelay = Number(draft?.StartDelaySec);
  const priority = Number(draft?.Priority);

  if (!Number.isFinite(imageDuration) || imageDuration < 4 || imageDuration > 299) {
    errors.ImageDurationSec = 'Czas wyświetlania zdjęć musi być w zakresie 4-299 sekund';
  }

  if (!Number.isFinite(moveSensitivity) || moveSensitivity < 0 || moveSensitivity > 99) {
    errors.MoveSensitivity = 'Czułość ruchu musi być w zakresie 0-99';
  }

  if (!Number.isFinite(startDelay) || startDelay < 4 || startDelay > 599) {
    errors.StartDelaySec = 'Idle time / start delay musi być w zakresie 4-599 sekund';
  }

  if (!Number.isFinite(priority) || priority < 0) {
    errors.Priority = 'Priorytet musi być liczbą >= 0';
  }

  return errors;
};

const validateAdvertise = (draft, getScreenSummary, options = {}) =>
  Object.values(getAdvertiseValidation(draft, getScreenSummary, options));

const getStatusBadge = (status) => {
  const normalizedStatus = normalizePublishStatus(status);
  const meta = STATUS_META[normalizedStatus] || STATUS_META.NotPublished;

  return <span className={meta.className}>{meta.label}</span>;
};

const getErrorMessage = (err) =>
  err?.response?.data?.message ||
  err?.data?.message ||
  err?.sqlMessage ||
  err?.message ||
  'Wystąpił błąd';

const getAdvertiseFilesBasePath = (clientId) => {
  const normalizedClientId = String(clientId || '').trim() || 'dev';
  return `images/${normalizedClientId}/ads`;
};

const normalizeAdvertiseFromList = (row, clientId) => {
  const terminalIds = parseMaybeJSON(row.TerminalIds, [])
    .map((value) => Number(value))
    .filter(Number.isFinite);

  const fileNames = parseMaybeJSON(row.FileNames, []);

  const filesMeta = fileNames.map((name, index) =>
    normalizeExistingAdvertiseFileSafe(
      {
        OriginalFileName: name,
        FileExt: getExtension(name),
        FileSize: 0,
        MimeType: '',
        FilePath: `${getAdvertiseFilesBasePath(clientId)}/${name}`,
        StoredFileName: name,
        SortOrder: index + 1,
      },
      index
    )
  );

  return {
    AdvertiseId: Number(row.AdvertiseId),
    Name: row.Name || '',
    Priority: Number(row.Priority || 0),
    StartDelaySec: Number(row.StartDelaySec || 15),
    ImageDurationSec: Number(row.ImageDurationSec || 30),
    MoveSensitivity: Number(row.MoveSensitivity || 0),
    ShowMessage: toBool(row.ShowMessage),
    IsActive: toBool(row.IsActive),
    PublishStatus: normalizePublishStatus(row.PublishStatus),
    Comment: row.Comment || '',
    TerminalIds: terminalIds,
    TerminalSerials: [],
    FilesMeta: filesMeta,
    FilesCount: Number(row.FilesCount || filesMeta.length || 0),
    TerminalsCount: Number(row.TerminalsCount || terminalIds.length || 0),
    PackageType: row.PackageType || getAdvertiseType(filesMeta),
    ScreenRatioKey: row.ScreenRatioKey || '',
    CreateDateTime: row.CreateDateTime || null,
    CreateUserId: Number(row.CreateUserId || 0) || null,
    UpdateDateTime: row.UpdateDateTime || null,
    UpdateUserId: Number(row.UpdateUserId || 0) || null,
  };
};

const normalizeAdvertiseFromDetails = (row) => {
  const terminalIds = Array.isArray(row?.TerminalIds)
    ? row.TerminalIds.map((value) => Number(value)).filter(Number.isFinite)
    : [];

  const filesMeta = Array.isArray(row?.Files)
    ? row.Files.map((file, index) => normalizeExistingAdvertiseFileSafe(file, index))
    : [];

  return {
    ...createEmptyAdvertise(),
    AdvertiseId: Number(row?.AdvertiseId || 0) || null,
    Name: row?.Name || '',
    Priority: Number(row?.Priority || 0),
    StartDelaySec: Number(row?.StartDelaySec || 15),
    ImageDurationSec: Number(row?.ImageDurationSec || 30),
    MoveSensitivity: Number(row?.MoveSensitivity || 0),
    ShowMessage: toBool(row?.ShowMessage),
    IsActive: toBool(row?.IsActive),
    PublishStatus: normalizePublishStatus(row?.PublishStatus),
    Comment: row?.Comment || '',
    TerminalIds: terminalIds,
    TerminalSerials: [],
    FilesMeta: filesMeta,
    PackageType: row?.PackageType || getAdvertiseType(filesMeta),
    ScreenRatioKey: row?.ScreenRatioKey || '',
    CreateDateTime: row?.CreateDateTime || null,
    CreateUserId: Number(row?.CreateUserId || 0) || null,
    UpdateDateTime: row?.UpdateDateTime || null,
    UpdateUserId: Number(row?.UpdateUserId || 0) || null,
  };
};

const prepareDraftForSubmit = (draft, getScreenSummary, getTerminalById = null) => {
  const screen = getScreenSummary(draft?.TerminalIds || []);

  const terminalSerials = (draft?.TerminalIds || [])
    .map((terminalId) => (getTerminalById ? getTerminalById(terminalId) : null))
    .filter((terminal) => terminal && !terminal.IsFallback)
    .map((terminal) => ({
      TerminalId: Number(terminal.TerminalId || 0) || null,
      SerialNo: String(
        terminal.SerialNo ||
          terminal.SerialNumber ||
          terminal.serialNo ||
          terminal.serialNumber ||
          ''
      ).trim(),
    }))
    .filter((terminal) => terminal.SerialNo);

  const normalizedFiles = (draft?.FilesMeta || []).map((file, index) =>
    normalizeFileForSubmit(file, index)
  );

  return {
    ...draft,
    FilesMeta: normalizedFiles,
    PackageType: getAdvertiseType(normalizedFiles),
    ScreenRatioKey: screen.key === 'NONE' || screen.key === 'MIXED' ? '' : screen.key,
    TerminalSerials: terminalSerials,
  };
};

const getFilePreviewSourceFile = (file) => file?.file || file?.rawFile || file?.sourceFile || null;

const getFilePreviewPath = (file, clientId) => {
  const directPath = String(file?.FilePath || file?.Path || file?.url || file?.Url || '').trim();

  if (directPath) {
    return buildApiMediaUrl(directPath);
  }

  const fileName = String(
    file?.StoredFileName || file?.OriginalFileName || file?.name || file?.FileName || ''
  ).trim();

  if (!fileName) return '';

  return buildApiMediaUrl(`${getAdvertiseFilesBasePath(clientId)}/${fileName}`);
};

const getPreviewDurationMs = (durationSec) => {
  const seconds = Number(durationSec);

  if (!Number.isFinite(seconds) || seconds <= 0) return 5000;

  return Math.max(1000, seconds * 1000);
};

const getDisplayDurationSec = (durationSec) =>
  Math.max(1, Math.round(getPreviewDurationMs(durationSec) / 1000));

const getFileCycleKey = (file, index) =>
  String(
    file?.key ||
      file?.AdvertiseFileId ||
      file?.FileId ||
      file?.StoredFileName ||
      file?.OriginalFileName ||
      file?.name ||
      index
  );

const normalizeResolutionValue = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace('×', 'x');

const getPreviewDeviceType = (screen) => {
  const resolution = normalizeResolutionValue(screen?.resolution);
  const key = String(screen?.key || '').trim();

  if (resolution === '480x480' || key === '1:1') return 'N62';
  if (resolution === '720x1280' || key === '9:16') return 'UN20';

  return 'UN20';
};

const getTerminalResolutionGroup = (terminal) => {
  const resolution = normalizeResolutionValue(terminal?.Resolution || terminal?.ScreenResolution);
  const key = String(terminal?.ScreenKey || terminal?.ScreenRatioKey || '').trim();

  if (resolution === '480x480' || key === '1:1') return '480x480';
  if (resolution === '720x1280' || key === '9:16') return '720x1280';

  return resolution || key || '';
};

const getResolutionGroupFromTerminalIds = (terminalIds, getTerminalById) => {
  if (!terminalIds?.length) return '';

  const firstTerminal = terminalIds
    .map((id) => getTerminalById(id))
    .find(Boolean);

  return getTerminalResolutionGroup(firstTerminal);
};

const isTerminalResolutionCompatible = (selectedTerminalIds, candidateTerminal, getTerminalById) => {
  if (!selectedTerminalIds?.length) return true;

  const firstSelectedTerminal = selectedTerminalIds
    .map((id) => getTerminalById(id))
    .find(Boolean);

  const selectedResolution = getTerminalResolutionGroup(firstSelectedTerminal);
  const candidateResolution = getTerminalResolutionGroup(candidateTerminal);

  if (!selectedResolution || !candidateResolution) return true;

  return selectedResolution === candidateResolution;
};

const normalizeSerialForImport = (value) =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '');

const getSerialFromImportedItem = (item) => {
  if (typeof item === 'string' || typeof item === 'number') {
    return normalizeSerialForImport(item);
  }

  return normalizeSerialForImport(
    item?.SerialNo ||
      item?.serialNo ||
      item?.SerialNumber ||
      item?.serialNumber ||
      item?.TerminalSerialNo ||
      item?.terminalSerialNo ||
      item?.Terminal ||
      item?.terminal ||
      ''
  );
};

const extractSerialsFromImportText = (text) => {
  const rawText = String(text || '').trim();

  if (!rawText) return [];

  const fromJsonValue = (value) => {
    if (Array.isArray(value)) {
      return value.map(getSerialFromImportedItem).filter(Boolean);
    }

    if (value && typeof value === 'object') {
      const candidate =
        value.TerminalSerials ||
        value.terminalSerials ||
        value.Serials ||
        value.serials ||
        value.Terminals ||
        value.terminals ||
        value.data;

      if (Array.isArray(candidate)) {
        return candidate.map(getSerialFromImportedItem).filter(Boolean);
      }

      return [getSerialFromImportedItem(value)].filter(Boolean);
    }

    return [];
  };

  try {
    const parsed = JSON.parse(rawText);
    const jsonSerials = fromJsonValue(parsed);

    if (jsonSerials.length) {
      return [...new Set(jsonSerials)];
    }
  } catch (err) {}

  const quotedSerials = [];
  const quotedRegex = /["']([^"']+)["']/g;
  let quotedMatch = quotedRegex.exec(rawText);

  while (quotedMatch) {
    const serial = normalizeSerialForImport(quotedMatch[1]);

    if (serial) {
      quotedSerials.push(serial);
    }

    quotedMatch = quotedRegex.exec(rawText);
  }

  if (quotedSerials.length) {
    return [...new Set(quotedSerials)];
  }

  return [
    ...new Set(
      rawText
        .split(/[^a-zA-Z0-9_-]+/)
        .map(normalizeSerialForImport)
        .filter((serial) => serial.length >= 4)
    ),
  ];
};

function AdvertisePreview({ files = [], screen, durationSec = 5, clientId }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewSourceMap, setPreviewSourceMap] = useState({});
  const [remainingSec, setRemainingSec] = useState(() => getDisplayDurationSec(durationSec));

  const displayDurationSec = useMemo(() => getDisplayDurationSec(durationSec), [durationSec]);

  const previewFiles = useMemo(
    () => (Array.isArray(files) ? files.filter(Boolean) : []),
    [files]
  );

  const filesSignature = useMemo(
    () => previewFiles.map((file, index) => getFileCycleKey(file, index)).join('|'),
    [previewFiles]
  );

  useEffect(() => {
    setActiveIndex(0);
    setRemainingSec(displayDurationSec);
  }, [filesSignature, displayDurationSec]);

  useEffect(() => {
    if (!previewFiles.length) {
      setActiveIndex(0);
      setRemainingSec(displayDurationSec);
      return;
    }

    if (activeIndex > previewFiles.length - 1) {
      setActiveIndex(0);
      setRemainingSec(displayDurationSec);
    }
  }, [activeIndex, previewFiles.length, displayDurationSec]);

  useEffect(() => {
    setRemainingSec(displayDurationSec);
  }, [activeIndex, displayDurationSec]);

  useEffect(() => {
    const createdUrls = [];
    const nextSourceMap = {};

    previewFiles.forEach((item, index) => {
      const key = getFileCycleKey(item, index);
      const sourceFile = getFilePreviewSourceFile(item);

      if (sourceFile) {
        const url = URL.createObjectURL(sourceFile);
        createdUrls.push(url);
        nextSourceMap[key] = url;
        return;
      }

      nextSourceMap[key] = getFilePreviewPath(item, clientId);
    });

    setPreviewSourceMap(nextSourceMap);

    return () => {
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [filesSignature, previewFiles, clientId]);

  useEffect(() => {
    if (previewFiles.length <= 1) {
      setRemainingSec(displayDurationSec);
      return undefined;
    }

    const countdownInterval = window.setInterval(() => {
      setRemainingSec((prev) => {
        if (prev <= 1) return 1;

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(countdownInterval);
  }, [activeIndex, displayDurationSec, filesSignature, previewFiles.length]);

  useEffect(() => {
    if (previewFiles.length <= 1) return undefined;

    const changeInterval = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % previewFiles.length);
    }, getPreviewDurationMs(durationSec));

    return () => window.clearInterval(changeInterval);
  }, [durationSec, filesSignature, previewFiles.length]);

  const getPreviewInfo = useCallback(
    (item, index) => {
      const key = getFileCycleKey(item, index);
      const ext = String(item?.ext || item?.FileExt || getExtension(item?.name || '') || '')
        .replace('.', '')
        .toLowerCase();

      const src = previewSourceMap[key] || getFilePreviewPath(item, clientId);

      return {
        key,
        ext,
        src,
      };
    },
    [previewSourceMap, clientId]
  );

  const file = previewFiles[activeIndex] || null;
  const currentPreview = file ? getPreviewInfo(file, activeIndex) : null;

  const ext = currentPreview?.ext || '';
  const previewSrc = currentPreview?.src || '';

  const preloadItems = useMemo(() => {
    if (previewFiles.length <= 1) return [];

    const indexesToPreload = [];

    for (let offset = 1; offset <= Math.min(2, previewFiles.length - 1); offset += 1) {
      const nextIndex = (activeIndex + offset) % previewFiles.length;

      if (!indexesToPreload.includes(nextIndex)) {
        indexesToPreload.push(nextIndex);
      }
    }

    return indexesToPreload
      .map((index) => {
        const item = previewFiles[index];
        const info = getPreviewInfo(item, index);

        return {
          ...info,
          index,
        };
      })
      .filter((item) => item.src && (item.ext === 'mp4' || IMAGE_EXTENSIONS.includes(item.ext)));
  }, [activeIndex, previewFiles, getPreviewInfo]);

  const isSquare = screen?.key === '1:1' || normalizeResolutionValue(screen?.resolution) === '480x480';
  const previewDeviceType = getPreviewDeviceType(screen);
  const hasPreviewFile = !!previewSrc && (ext === 'mp4' || IMAGE_EXTENSIONS.includes(ext));

  const previewContent = (
    <>
      {hasPreviewFile && (
        <div
          className="advertise-preview-font"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            letterSpacing: 0.4,
            textAlign: 'center',
            pointerEvents: 'none',
            fontWeight: 800,
            fontSize: 9,
            color: '#000',
            backgroundColor: '#fff',
          }}
        >
          <span>Dotknij aby </span>
          <span style={{ color: '#ff7a00' }}>rozpocząć</span>
        </div>
      )}

      {previewSrc && ext === 'mp4' ? (
        <video
          key={`${currentPreview?.key || getFileCycleKey(file, activeIndex)}-${activeIndex}`}
          src={previewSrc}
          muted
          loop
          autoPlay
          playsInline
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            background: '#000',
          }}
        />
      ) : previewSrc && IMAGE_EXTENSIONS.includes(ext) ? (
        <img
          key={`${currentPreview?.key || getFileCycleKey(file, activeIndex)}-${activeIndex}`}
          src={previewSrc}
          alt={file?.name || file?.OriginalFileName || 'Podgląd reklamy'}
          loading="eager"
          decoding="sync"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            background: '#000',
          }}
        />
      ) : (
        <div
          className="text-center text-muted d-flex flex-column justify-content-center align-items-center"
          style={{
            height: '100%',
            padding: 16,
          }}
        >
          <i className="far fa-image mb-2" style={{ fontSize: 34 }} />
          <div className="small pe-none">Dodaj plik, żeby zobaczyć podgląd</div>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(120deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 24%, rgba(255,255,255,0) 45%)',
        }}
      />
    </>
  );

  return (
    <div
      className="border rounded p-3 bg-light"
      style={{
        minHeight: 320,
        position: 'relative',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
      }}
    >
      {preloadItems.map((item) =>
        item.ext === 'mp4' ? (
          <video
            key={`preload-video-${item.key}-${item.index}`}
            src={item.src}
            preload="auto"
            muted
            playsInline
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          />
        ) : (
          <img
            key={`preload-image-${item.key}-${item.index}`}
            src={item.src}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          />
        )
      )}

      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <div className="font-weight-bold advertise-font pe-none">Podgląd reklam</div>
            <div className="text-muted small advertise-font-small pe-none">
              {file ? file.name || file.OriginalFileName : '\u00A0'}
            </div>
        </div>

        <div className="text-right">
          <span className="font-weight-bold advertise-font pe-none">{screen?.resolution || ''}</span>
          {previewFiles.length > 1 ? (
            <div className="text-muted small advertise-margin-right advertise-font-small pe-none">
              {activeIndex + 1}/{previewFiles.length}
            </div>
          ) : null}
        </div>
      </div>

      {previewDeviceType === 'N62' ? (
        <div className="d-flex justify-content-center">
          <div
            style={{
              width: 220,
              aspectRatio: '1/1',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 28,
              padding: '32px 20px 32px 20px',
              background: '#6a6b6e',
              boxShadow: '0 4px 10px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.2)',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 17,
                right: 48,
                display: 'flex',
                gap: 2,
                zIndex: 10,
              }}
            >
              {[0, 1, 2].map((dot) => (
                <span
                  key={dot}
                  style={{
                    width: 8,
                    height: 4,
                    borderRadius: 4,
                    background: '#202124',
                    boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.18), 0 1px 1px rgba(0,0,0,0.45)',
                    display: 'block',
                  }}
                />
              ))}
            </div>

            <div
              style={{
                position: 'absolute',
                left: '50%',
                bottom: 0,
                transform: 'translateX(-50%)',
                width: 134,
                height: 20,
                borderRadius: '12px 12px 0px 0px',
                background: '#242526',
                boxShadow: 'inset 0 3px 6px rgba(0,0,0,0.65), 0 -1px 0 rgba(255,255,255,0.12)',
                zIndex: 2,
              }}
            />

            <div
              style={{
                overflow: 'visible',
                padding: '16px 10px 16px 10px',
                borderRadius: 14,
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0)), #050505',
                aspectRatio: '1 / 1',
                position: 'relative',
                border: '1px solid rgba(255,255,255,0.08)',
                zIndex: 4,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 4,
                  transform: 'translateX(-50%)',
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: '#111',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: 'inset 0 0 2px rgba(255,255,255,0.2), 0 0 2px rgba(0,0,0,0.8)',
                  zIndex: 8,
                }}
              />

              <div
                style={{
                  overflow: 'hidden',
                  borderRadius: 0,
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0)), #050505',
                  aspectRatio: '1 / 1',
                  position: 'relative',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {previewContent}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-center">
          <div
            style={{
              width: isSquare ? 240 : 180,
              maxWidth: '100%',
              border: '3px solid #6a6b6e',
              borderRadius: 14,
              padding: '28px 28px 24px 28px',
              background: '#000',
              boxShadow: '0 4px 10px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.08)',
            }}
          >
            <div
              style={{
                overflow: 'hidden',
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0)), #050505',
                aspectRatio: isSquare ? '1 / 1' : '9 / 16',
                position: 'relative',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {previewContent}
            </div>
          </div>
        </div>
      )}

      {previewFiles.length > 1 && hasPreviewFile ? (
        <div className="text-center text-muted small mt-3 pe-none">
          Zmiana reklamy za:{' '}
          <span className="font-weight-bold pe-none">{remainingSec}</span>
        </div>
      ) : null}
    </div>
  );
}

function AdvertiseModal({
  draft,
  setDraft,
  onClose,
  onSave,
  onToggleActive,
  mode = 'new',
  isSaving = false,
  terminalsCatalog = [],
  getTerminalById,
  getScreenSummary,
  clientId,
}) {
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);

  const requiresActiveStatus =
    draft?.PublishStatus === 'ToPublish' || draft?.PublishStatus === 'Published';

  const fieldErrors = useMemo(
    () =>
      getAdvertiseValidation(draft, getScreenSummary, {
        allowInactive: !requiresActiveStatus,
        requirePublishTargets: false,
      }),
    [draft, getScreenSummary, requiresActiveStatus]
  );

  const validationErrors = useMemo(() => Object.values(fieldErrors), [fieldErrors]);

  const screen = useMemo(
    () => getScreenSummary(draft.TerminalIds),
    [draft.TerminalIds, getScreenSummary]
  );

  const packageType = useMemo(() => getAdvertiseType(draft.FilesMeta), [draft.FilesMeta]);

  const previewFiles = useMemo(() => draft.FilesMeta || [], [draft.FilesMeta]);
  const hasSelectedTerminal = Array.isArray(draft?.TerminalIds) && draft.TerminalIds.length > 0;
  const [terminalUndoIds, setTerminalUndoIds] = useState([]);
  const [lastTerminalIdsBeforeEmpty, setLastTerminalIdsBeforeEmpty] = useState([]);

  const [lastPreviewScreen, setLastPreviewScreen] = useState(() => {
    const initialScreen = getScreenSummary(draft?.TerminalIds || []);

    if (initialScreen?.key && initialScreen.key !== 'NONE') {
      return initialScreen;
    }

    return null;
  });

  useEffect(() => {
    if (screen?.key && screen.key !== 'NONE') {
      setLastPreviewScreen(screen);
    }
  }, [screen]);

  const previewScreen = useMemo(() => {
    if (screen?.key === 'NONE' && lastPreviewScreen) {
      return lastPreviewScreen;
    }

    return screen;
  }, [screen, lastPreviewScreen]);

  const setField = (field) => (e) => {
    const raw = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setDraft((prev) => ({ ...prev, [field]: raw }));
  };

  const hasError = (field) => !!fieldErrors[field];

  const handleClearTerminals = () => {
    const currentTerminalIds = Array.isArray(draft?.TerminalIds) ? draft.TerminalIds : [];

    if (!currentTerminalIds.length || isSaving) return;

    setTerminalUndoIds([...currentTerminalIds]);
    setLastTerminalIdsBeforeEmpty([...currentTerminalIds]);

    setDraft((prev) => ({
      ...prev,
      TerminalIds: [],
    }));
  };

  const handleUndoClearTerminals = () => {
    if (!terminalUndoIds.length || isSaving) return;

    setDraft((prev) => ({
      ...prev,
      TerminalIds: [...terminalUndoIds],
    }));

    setTerminalUndoIds([]);
    setLastTerminalIdsBeforeEmpty([]);
  };

  const showResolutionWarning = (terminal) => {
    const firstTerminal = draft.TerminalIds
      .map((id) => getTerminalById(id))
      .find(Boolean);

    NotificationManager.warning(
      `Nie możesz mieszać terminali o różnych rozdzielczościach. Najpierw odznacz terminal ${firstTerminal?.Resolution || ''}, żeby wybrać ${terminal?.Resolution || ''}`,
      'Różne rozdzielczości',
      3500
    );
  };

  const notifyFilesClearedByResolutionChange = (fromResolution, toResolution) => {
    NotificationManager.info(
      `Pliki reklamowe zostały wyczyszczone, ponieważ zmieniono rozdzielczość terminali z ${fromResolution} na ${toResolution}`,
      'Zmieniono rozdzielczość',
      3500
    );
  };

    const importTerminalsFromText = (text) => {
    const serials = extractSerialsFromImportText(text);

    if (!serials.length) {
      NotificationManager.error(
        'Nie znaleziono numerów seryjnych w importowanym pliku',
        'Import',
        3500
      );
      return;
    }

    const terminalBySerial = new Map(
      terminalsCatalog
        .map((terminal) => [
          normalizeSerialForImport(
            terminal?.SerialNo ||
              terminal?.SerialNumber ||
              terminal?.TerminalSerialNo ||
              terminal?.serialNo ||
              terminal?.serialNumber ||
              ''
          ),
          terminal,
        ])
        .filter(([serial]) => serial)
    );

    const matchedTerminals = [];
    const missingSerials = [];

    serials.forEach((serial) => {
      const terminal = terminalBySerial.get(serial);

      if (terminal) {
        matchedTerminals.push(terminal);
      } else {
        missingSerials.push(serial);
      }
    });

    const uniqueTerminals = Array.from(
      new Map(
        matchedTerminals
          .filter((terminal) => Number.isFinite(Number(terminal?.TerminalId)))
          .map((terminal) => [Number(terminal.TerminalId), terminal])
      ).values()
    );

    if (!uniqueTerminals.length) {
      NotificationManager.error(
        `Nie znaleziono terminali dla importowanych seriali. Liczba seriali: ${serials.length}`,
        'Import',
        4500
      );
      return;
    }

    const groupsByResolution = new Map();

    uniqueTerminals.forEach((terminal) => {
      const resolutionGroup = getTerminalResolutionGroup(terminal) || 'unknown';

      if (!groupsByResolution.has(resolutionGroup)) {
        groupsByResolution.set(resolutionGroup, []);
      }

      groupsByResolution.get(resolutionGroup).push(terminal);
    });

    const resolutionGroups = Array.from(groupsByResolution.entries()).sort(
      (a, b) => b[1].length - a[1].length
    );

    const selectedTerminals = resolutionGroups[0]?.[1] || [];
    const skippedByResolution = uniqueTerminals.length - selectedTerminals.length;

    if (skippedByResolution > 0) {
      NotificationManager.warning(
        `Import zawiera terminale o różnych rozdzielczościach. Wybrano największą grupę: ${selectedTerminals.length}, pominięto: ${skippedByResolution}`,
        'Różne rozdzielczości',
        6000
      );
    }

    const nextTerminalIds = selectedTerminals
      .map((terminal) => Number(terminal.TerminalId))
      .filter(Number.isFinite);

    const previousResolution = getResolutionGroupFromTerminalIds(
      draft.TerminalIds || [],
      getTerminalById
    );

    const nextResolution = getResolutionGroupFromTerminalIds(nextTerminalIds, getTerminalById);

    const shouldClearFiles =
      !!previousResolution &&
      !!nextResolution &&
      previousResolution !== nextResolution &&
      !!draft.FilesMeta?.length;

    if (shouldClearFiles) {
      notifyFilesClearedByResolutionChange(previousResolution, nextResolution);
    }

    setTerminalUndoIds([]);
    setLastTerminalIdsBeforeEmpty([]);

    setDraft((prev) => ({
      ...prev,
      TerminalIds: nextTerminalIds,
      FilesMeta: shouldClearFiles ? [] : prev.FilesMeta,
    }));

    NotificationManager.success(
      `Zaimportowano terminale: ${nextTerminalIds.length}${
        missingSerials.length ? `. Nie znaleziono: ${missingSerials.length}` : ''
      }`,
      'Import',
      4500
    );
  };

  const handleImportTerminalsFile = async (e) => {
    const file = e.target.files?.[0];

    e.target.value = '';

    if (!file || isSaving) return;

    try {
      const text = await file.text();
      importTerminalsFromText(text);
    } catch (err) {
      NotificationManager.error(
        err?.message || 'Nie udało się odczytać pliku importu',
        'Import',
        3500
      );
    }
  };

  const toggleTerminal = (terminalId) => {
    const currentTerminalIds = Array.isArray(draft?.TerminalIds) ? draft.TerminalIds : [];
    const exists = currentTerminalIds.includes(terminalId);
    const terminal = getTerminalById(terminalId);

    if (!exists && currentTerminalIds.length > 0) {
      const isCompatible = isTerminalResolutionCompatible(
        currentTerminalIds,
        terminal,
        getTerminalById
      );

      if (!isCompatible) {
        showResolutionWarning(terminal);
        return;
      }
    }

    const nextTerminalIds = exists
      ? currentTerminalIds.filter((id) => id !== terminalId)
      : [...currentTerminalIds, terminalId];

    const referenceTerminalIds = currentTerminalIds.length
      ? currentTerminalIds
      : terminalUndoIds.length
        ? terminalUndoIds
        : lastTerminalIdsBeforeEmpty;

    const previousResolution = getResolutionGroupFromTerminalIds(
      referenceTerminalIds,
      getTerminalById
    );

    const nextResolution = getResolutionGroupFromTerminalIds(nextTerminalIds, getTerminalById);

    const shouldClearFiles =
      !!previousResolution &&
      !!nextResolution &&
      previousResolution !== nextResolution &&
      !!draft.FilesMeta?.length;

    if (shouldClearFiles) {
      notifyFilesClearedByResolutionChange(previousResolution, nextResolution);
    }

    setTerminalUndoIds([]);

    if (!nextTerminalIds.length && currentTerminalIds.length) {
      setLastTerminalIdsBeforeEmpty([...currentTerminalIds]);
    } else {
      setLastTerminalIdsBeforeEmpty([]);
    }

    setDraft((prev) => ({
      ...prev,
      TerminalIds: nextTerminalIds,
      FilesMeta: shouldClearFiles ? [] : prev.FilesMeta,
    }));
  };

  const handlePickFiles = async (e) => {
    const rawFiles = Array.from(e.target.files || []);

    e.target.value = '';

    if (!rawFiles.length) return;

    const pickedFiles = await Promise.all(
      rawFiles.map((file, index) => normalizeNewAdvertiseFileWithDimensions(file, index))
    );

    const currentScreen = getScreenSummary(draft.TerminalIds || []);
    const validationScreen =
      currentScreen?.key === 'NONE' && lastPreviewScreen ? lastPreviewScreen : currentScreen;

    const acceptedFiles = [];
    const rejectedErrors = [];

    pickedFiles.forEach((file) => {
      const screenError = getFileScreenValidationError(file, validationScreen);

      if (screenError) {
        rejectedErrors.push(screenError);
        return;
      }

      acceptedFiles.push(file);
    });

    if (rejectedErrors.length) {
      NotificationManager.error(rejectedErrors[0], 'Błąd', 4500);
    }

    if (!acceptedFiles.length) return;

    setDraft((prev) => ({
      ...prev,
      FilesMeta: [...(prev.FilesMeta || []), ...acceptedFiles].map((file, index) => ({
        ...file,
        SortOrder: index + 1,
      })),
    }));
  };

  const handleRemoveFile = (fileKey, fileIndex) => {
    setDraft((prev) => ({
      ...prev,
      FilesMeta: (prev.FilesMeta || [])
        .filter((file, index) => {
          if (fileKey !== undefined && fileKey !== null && fileKey !== '') {
            return String(file.key || '') !== String(fileKey);
          }

          return index !== fileIndex;
        })
        .map((file, index) => ({ ...file, SortOrder: index + 1 })),
    }));
  };

  const handleClearFiles = () => {
    setDraft((prev) => ({
      ...prev,
      FilesMeta: [],
    }));
  };

  return (
    <Modal show onHide={onClose} centered backdrop="static" size="xl" scrollable>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>
          {mode === 'edit' ? 'Edytuj zestaw reklam' : 'Nowy zestaw reklam'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="custom-scroll">
        <div className="alert alert-info mb-3 pe-none">
          <div>
            <strong>Obsługiwane pliki:</strong> .mp4, .jpg, .jpeg, .png, .bmp
          </div>
          <div>
            <strong>Maksymalny rozmiar pliku:</strong> {MAX_FILE_SIZE_MB} MB
          </div>
          <div>
            <strong>Maksymalna liczba plików:</strong> {MAX_TOTAL_FILES}
          </div>
          <div>
            <strong>Walidacja ekranu:</strong> plik musi mieć proporcje i rozdzielczość zgodną
            z wybranymi terminalami
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-xl-8">
            <div className="border rounded p-3 mb-3 bg-white">
              <div className="font-weight-bold advertise-font pe-none">Ustawienia reklam</div>
              <div className="row">
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="advertise-font">Nazwa zestawu</label>
                    <input
                      className={`form-control ${hasError('Name') ? 'is-invalid' : ''}`}
                      value={draft.Name}
                      onChange={setField('Name')}
                      maxLength={200}
                    />
                    {hasError('Name') && (
                      <div className="invalid-feedback d-block">{fieldErrors.Name}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-3 col-md-6 status-margin-fix">
                  <div className="form-group">
                    <label className="advertise-font status-header-fix">Status</label>
                    <select
                      className="form-control advertise-font-small form-control-height"
                      value={draft.PublishStatus}
                      onChange={setField('PublishStatus')}
                    >
                      {Object.keys(STATUS_META).map((key) => (
                        <option className="advertise-font-small" key={key} value={key}>
                          {STATUS_META[key].label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="form-group">
                    <label className="advertise-font">Typ zestawu</label>
                    <input
                      className="form-control"
                      value={packageType === 'Empty' ? '' : packageType}
                      readOnly
                    />
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="form-group">
                    <label className="advertise-font">Priorytet</label>
                    <input
                      type="number"
                      min="0"
                      className={`form-control ${hasError('Priority') ? 'is-invalid' : ''}`}
                      value={draft.Priority}
                      onChange={setField('Priority')}
                    />
                    {hasError('Priority') && (
                      <div className="invalid-feedback d-block">{fieldErrors.Priority}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="form-group">
                    <label className="advertise-font">Czas bezczynności (s)</label>
                    <input
                      type="number"
                      min="4"
                      max="599"
                      className={`form-control ${hasError('StartDelaySec') ? 'is-invalid' : ''}`}
                      value={draft.StartDelaySec}
                      onChange={setField('StartDelaySec')}
                    />
                    {hasError('StartDelaySec') && (
                      <div className="invalid-feedback d-block">{fieldErrors.StartDelaySec}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="form-group">
                    <label className="advertise-font">Czas trwania (s)</label>
                    <input
                      type="number"
                      min="4"
                      max="299"
                      className={`form-control ${hasError('ImageDurationSec') ? 'is-invalid' : ''}`}
                      value={draft.ImageDurationSec}
                      onChange={setField('ImageDurationSec')}
                    />
                    {hasError('ImageDurationSec') && (
                      <div className="invalid-feedback d-block">{fieldErrors.ImageDurationSec}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-3 col-md-6">
                  <div className="form-group">
                    <label className="advertise-font">Czułość ruchu</label>
                    <input
                      type="number"
                      min="0"
                      max="99"
                      className={`form-control ${hasError('MoveSensitivity') ? 'is-invalid' : ''}`}
                      value={draft.MoveSensitivity}
                      onChange={setField('MoveSensitivity')}
                    />
                    {hasError('MoveSensitivity') && (
                      <div className="invalid-feedback d-block">{fieldErrors.MoveSensitivity}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-group d-flex align-items-center flex-wrap mb-2">
                    <div className="d-flex align-items-center ml-1">
                      <input
                        id="isActive"
                        type="checkbox"
                        checked={toBool(draft.IsActive)}
                        onChange={setField('IsActive')}
                        className="mr-2"
                      />
                      <label htmlFor="isActive" className="mb-0">
                        Aktywny
                      </label>
                    </div>

                    {hasError('IsActive') && (
                      <div className="w-100 text-danger small mt-1">{fieldErrors.IsActive}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-group mb-0">
                    <label className="advertise-font">Komentarz</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={draft.Comment}
                      onChange={setField('Comment')}
                      maxLength={1000}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border rounded p-3 mb-3 bg-white">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center flex-wrap">
                <label className="font-weight-bold advertise-font mb-0 mr-3">Terminale</label>

                <input
                  ref={importInputRef}
                  type="file"
                  accept=".json,.txt"
                  style={{ display: 'none' }}
                  onChange={handleImportTerminalsFile}
                />

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm mr-2 terminals-text-fix"
                  onClick={() => importInputRef.current?.click()}
                  disabled={isSaving || !terminalsCatalog.length}
                >
                  Import
                </button>

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm mr-2 terminals-text-fix"
                  onClick={handleUndoClearTerminals}
                  disabled={!terminalUndoIds.length || isSaving}
                >
                  Cofnij
                </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm terminals-text-fix"
                    onClick={handleClearTerminals}
                    disabled={!draft.TerminalIds?.length || isSaving}
                  >
                    Wyczyść
                  </button>
                </div>

                <span className="font-weight-bold advertise-font-small advertise-margin-right pe-none">
                  {screen.key === 'NONE' || !screen.resolution || screen.resolution === '-'
                    ? screen.label
                    : `${screen.label} • ${screen.resolution}`}
                </span>
              </div>

              <div
                className={`rounded p-2 custom-scroll ${
                  hasError('TerminalIds') ? 'border border-danger' : 'border'
                }`}
                style={{ maxHeight: 230, overflowY: 'auto' }}
              >
                {terminalsCatalog.length ? (
                  terminalsCatalog.map((terminal) => {
                    const checked = draft.TerminalIds.includes(terminal.TerminalId);
                    const blockedByResolution =
                      !checked &&
                      draft.TerminalIds.length > 0 &&
                      !isTerminalResolutionCompatible(
                        draft.TerminalIds,
                        terminal,
                        getTerminalById
                      );

                    return (
                      <label
                        key={terminal.TerminalId}
                        className="d-flex align-items-start border rounded p-2 mb-2 advertise-font"
                        onClick={(e) => {
                          if (!blockedByResolution) return;

                          e.preventDefault();
                          showResolutionWarning(terminal);
                        }}
                        style={{
                          cursor: blockedByResolution ? 'not-allowed' : 'pointer',
                          background: checked ? '#f4f9ff' : blockedByResolution ? '#f8f9fa' : '#fff',
                          boxShadow: checked ? 'inset 3px 0 0 #007bff' : 'none',
                          opacity: blockedByResolution ? 0.55 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={blockedByResolution || isSaving}
                          onChange={() => toggleTerminal(terminal.TerminalId)}
                          className="mt-1 mr-2"
                        />

                        <div>
                          <div className="font-weight-bold">{terminal.TerminalName}</div>
                          <div className="text-muted small">
                            {terminal.Resolution} • {terminal.RatioLabel}
                          </div>
                          {terminal.MachineFullName ? (
                            <div className="text-muted small">{terminal.MachineFullName}</div>
                          ) : null}
                        </div>
                      </label>
                    );
                  })
                ) : (
                  <div className="text-muted">Brak terminali do wyboru</div>
                )}
              </div>

              {hasError('TerminalIds') && (
                <div className="text-danger small mt-1">{fieldErrors.TerminalIds}</div>
              )}
            </div>
          </div>

          <div className="col-12 col-xl-4 mt-3 mt-xl-0">
            <AdvertisePreview
              files={previewFiles}
              screen={previewScreen}
              durationSec={draft.ImageDurationSec}
              clientId={clientId}
            />

            <div className="border rounded p-3 bg-white mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="font-weight-bold advertise-font mb-0">Pliki reklamowe</label>

                <div className="d-flex">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mr-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSaving || !hasSelectedTerminal}
                  >
                    Dodaj pliki
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleClearFiles}
                    disabled={!draft.FilesMeta?.length || isSaving}
                  >
                    Wyczyść
                  </button>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".mp4,.jpg,.jpeg,.png,.bmp"
                multiple
                style={{ display: 'none' }}
                onChange={handlePickFiles}
              />

              <div
                className={`rounded p-2 advertise-padding-left ${
                  hasError('FilesMeta') ? 'border border-danger' : 'border'
                }`}
                style={{ maxHeight: 210, overflowY: 'auto' }}
              >
                {draft.FilesMeta?.length ? (
                  draft.FilesMeta.map((file, index) => {
                    const dimensions = getFileDimensions(file);

                    return (
                      <div
                        key={file.key || `${file.name || file.OriginalFileName}-${index}`}
                        className="d-flex justify-content-between align-items-center border rounded px-2 py-2 mb-2"
                      >
                        <div className="pr-2">
                          <div className="font-weight-bold">
                            {file.name || file.OriginalFileName}
                          </div>
                          <div className="text-muted small advertise-font-tiny pe-none">
                            .{file.ext || file.FileExt} •{' '}
                            {bytesToMb(file.size ?? file.FileSize ?? 0)}
                            {dimensions ? (
                              <>
                                {' '}
                                • {dimensions.width}x{dimensions.height} •{' '}
                                {getRatioKeyFromDimensions(dimensions.width, dimensions.height)}
                              </>
                            ) : null}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveFile(file.key, index)}
                          disabled={isSaving}
                        >
                          Usuń
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-muted">Brak plików</div>
                )}
              </div>

              {hasError('FilesMeta') && (
                <div className="text-danger small mt-1">{fieldErrors.FilesMeta}</div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div>
          {mode === 'edit' && (
            <button
              type="button"
              className={toBool(draft.IsActive) ? 'btn btn-outline-danger' : 'btn btn-outline-success'}
              onClick={onToggleActive}
              disabled={isSaving}
            >
              {toBool(draft.IsActive) ? 'Dezaktywuj' : 'Aktywuj'}
            </button>
          )}
        </div>

        <div>
          <button type="button" className="btn btn-light mr-2" onClick={onClose} disabled={isSaving}>
            Anuluj
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={validationErrors.length > 0 || isSaving}
          >
            {isSaving ? 'Zapisywanie...' : mode === 'edit' ? 'Zapisz' : 'Dodaj zestaw'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default function Advertises() {
  const { fetchMssqlApi } = useFetch();
  const { setHeaderData } = useContext(NavigationContext);

  const clientId = (sessionStorage.getItem('clientId') || '').trim();
  const isConsole = clientId === 'console';

  const reportList = useMemo(
    () => [{ reportId: 109, reportColor: 'primary' }],
    []
  );

  const defaultFilter = useMemo(() => {
    const base = {
      page: 1,
      rowsPerPage: 50,
      sortBy: 'UpdateDateTime|DESC',
      search: '',
      publishStatus: '',
      packageType: '',
      screenRatioKey: '',
      isActive: 'true',
      terminalId: null,
      terminalSerialNo: '',
    };

    if (!isConsole) {
      return {
        ...base,
        customerId: null,
      };
    }

    return base;
  }, [isConsole]);

  const [advertises, setAdvertises] = useState([]);
  const [availableTerminals, setAvailableTerminals] = useState([]);
  const [editor, setEditor] = useState(null);
  const [editorMode, setEditorMode] = useState('new');
  const [isSaving, setIsSaving] = useState(false);
  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: false,
    flatView: false,
    rowsPerPage: 50,
  });

  const terminalMap = useMemo(
    () =>
      new Map(
        availableTerminals
          .filter((item) => Number.isFinite(Number(item?.TerminalId)))
          .map((item) => [Number(item.TerminalId), item])
      ),
    [availableTerminals]
  );

  const getTerminalById = useCallback(
    (id) => {
      const normalizedId = Number(id);
      if (!Number.isFinite(normalizedId)) return null;

      return terminalMap.get(normalizedId) || createFallbackTerminal(normalizedId);
    },
    [terminalMap]
  );

  const getScreenSummary = useCallback(
    (terminalIds = []) => {
      const terminals = terminalIds.map(getTerminalById).filter(Boolean);

      if (!terminals.length) {
        return {
          key: 'NONE',
          label: '',
          resolution: '',
          isMixed: false,
        };
      }

      const uniqueKeys = [...new Set(terminals.map((terminal) => terminal.ScreenKey || 'unknown'))];
      const uniqueResolutions = [
        ...new Set(terminals.map((terminal) => terminal.Resolution || '-')),
      ];

      if (uniqueKeys.length > 1) {
        return {
          key: 'MIXED',
          label: 'Mieszane proporcje',
          resolution: uniqueResolutions.join(', '),
          isMixed: true,
        };
      }

      const onlyKey = uniqueKeys[0];

      return {
        key: onlyKey,
        label: onlyKey === '1:1' ? '1:1' : onlyKey === '9:16' ? '9:16' : onlyKey,
        resolution: uniqueResolutions[0],
        isMixed: false,
      };
    },
    [getTerminalById]
  );

  const parseThreeState = (value) => {
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    if (value === false || value === 'false' || value === 0 || value === '0') return false;

    return null;
  };

  const apiRequest = (endpoint, options = {}) =>
    new Promise((resolve, reject) => {
      fetchMssqlApi(
        endpoint,
        { hideNotification: true, ...options },
        (res) => resolve(res),
        (err) => reject(err)
      );
    });

  const fetchTerminalsLikeTerminalsTab = useCallback(
    (terminalFilter = {}, onSuccess, onError, showLoader = false) => {
      fetchMssqlApi(
        'terminals',
        {
          method: 'POST',
          hideNotification: true,
          showLoader,
          data: {
            customerAbbr: clientId,
            customerId: terminalFilter?.customerId ?? null,
            terminalId: terminalFilter?.terminalId ?? null,
            terminalSerialNo: terminalFilter?.terminalSerialNo || '',
            terminalTypeId: terminalFilter?.terminalTypeId ?? null,
            machineId: terminalFilter?.consoleMachineId ?? terminalFilter?.machineId ?? null,
            sortBy: terminalFilter?.sortBy || null,
          },
        },
        (res) => onSuccess?.(res),
        (err) => onError?.(err)
      );
    },
    [fetchMssqlApi, clientId]
  );

  const getTerminalOptions = useCallback(() => {
    fetchTerminalsLikeTerminalsTab(
      {
        customerId: null,
        terminalId: null,
        terminalSerialNo: '',
        terminalTypeId: null,
        consoleMachineId: null,
        sortBy: 'SerialNo|ASC',
      },
      (res) => {
        const rows = Array.isArray(res) ? res : [];
        const mapped = rows.map(normalizeTerminalFromApi).filter(Boolean);

        const unique = Array.from(new Map(mapped.map((item) => [item.TerminalId, item])).values())
          .filter((item) => item.TerminalId)
          .sort((a, b) =>
            String(a?.TerminalName || '').localeCompare(String(b?.TerminalName || ''), 'pl', {
              numeric: true,
              sensitivity: 'base',
            })
          );

        setAvailableTerminals(unique);
      },
      () => setAvailableTerminals([]),
      false
    );
  }, [fetchTerminalsLikeTerminalsTab]);

  const getAdvertiseDetails = async (advertiseId) => {
    const res = await apiRequest(`advertise/${advertiseId}`, {
      method: 'GET',
    });

    return normalizeAdvertiseFromDetails(res || {});
  };

  const applyClientSideTerminalSerialFilter = useCallback(
    (items, f) => {
      const serialSearch = String(f?.terminalSerialNo || '').trim().toLowerCase();

      if (!serialSearch) return items;

      const matchingTerminalIds = availableTerminals
        .filter((terminal) =>
          String(terminal?.SerialNo || '').toLowerCase().includes(serialSearch)
        )
        .map((terminal) => Number(terminal.TerminalId))
        .filter(Number.isFinite);

      if (!matchingTerminalIds.length) return [];

      const matchingSet = new Set(matchingTerminalIds);

      return items.filter((item) =>
        (item.TerminalIds || []).some((terminalId) => matchingSet.has(Number(terminalId)))
      );
    },
    [availableTerminals]
  );

  const getAdvertises = useCallback(
    async (f = filter, pageSize, page) => {
      const rowsPerPage = pageSize ?? settingsFilter?.rowsPerPage ?? f?.rowsPerPage ?? 50;
      const pageNo = Math.max(1, Number(page ?? f?.page) || 1);

      fetchMssqlApi(
        'advertises',
        {
          method: 'POST',
          hideNotification: true,
          data: {
            search: String(f?.search || f?.advertiseName || '').trim(),
            publishStatus: String(f?.publishStatus || f?.status || '').trim(),
            packageType: String(f?.packageType || f?.type || '').trim(),
            screenRatioKey: String(f?.screenRatioKey || f?.screenKey || '').trim(),
            isActive: parseThreeState(f?.isActive),
            terminalId: f?.terminalId || null,
            pageNo,
            pageSize: rowsPerPage,
            sortBy: f?.sortBy,
          },
        },
        (res) => {
          const rows = Array.isArray(res) ? res : res ? [res] : [];
          const mapped = rows.map((row) => normalizeAdvertiseFromList(row, clientId));

          const activeFiltered =
            parseThreeState(f?.isActive) === null
              ? mapped
              : mapped.filter((item) => item.IsActive === parseThreeState(f?.isActive));

          setAdvertises(applyClientSideTerminalSerialFilter(activeFiltered, f));
        },
        (err) => {
          NotificationManager.error(getErrorMessage(err), 'Błąd', 3000);
        }
      );
    },
    [
      fetchMssqlApi,
      filter,
      settingsFilter,
      clientId,
      availableTerminals,
      applyClientSideTerminalSerialFilter,
    ]
  );

  useEffect(() => {
    setHeaderData({ text: 'Reklamy' });
  }, [setHeaderData]);

  useEffect(() => {
    setFilter(defaultFilter);
    getTerminalOptions();
    getAdvertises(defaultFilter, defaultFilter.rowsPerPage, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilter]);

  const sortState = useMemo(() => {
    const [field, dir] = String(filter.sortBy || '').split('|');

    return { field, dir: (dir || '').toUpperCase() };
  }, [filter.sortBy]);

  const arrow = (field) => {
    if (sortState.field !== field) return '';

    return sortState.dir === 'ASC' ? ' ▲' : ' ▼';
  };

  const handleHeaderSort = (field) => {
    const [currentField, currentDirRaw] = String(filter.sortBy || '').split('|');
    const currentDir = (currentDirRaw || '').toUpperCase();

    const nextDir =
      currentField === field ? (currentDir === 'ASC' ? 'DESC' : 'ASC') : sortDefaults[field] || 'ASC';

    const nextFilter = { ...filter, sortBy: `${field}|${nextDir}`, page: 1 };

    setFilter(nextFilter);
    getAdvertises(nextFilter, settingsFilter?.rowsPerPage, 1);
  };

  const openNew = () => {
    setEditorMode('new');
    setEditor(createEmptyAdvertise());
  };

  const openEdit = async (item) => {
    try {
      const fullAdvertise = await getAdvertiseDetails(item.AdvertiseId);

      setEditorMode('edit');
      setEditor(fullAdvertise);
    } catch (err) {
      NotificationManager.error(getErrorMessage(err), 'Błąd', 3000);
    }
  };

  const closeEditor = () => {
    setEditor(null);
    setEditorMode('new');
  };

  const saveAdvertise = async () => {
    if (!editor) return;

    const prepared = prepareDraftForSubmit(
      {
        ...editor,
        PublishStatus: editorMode === 'edit' ? 'NotPublished' : editor.PublishStatus,
      },
      getScreenSummary,
      getTerminalById
    );

    const isPublishing =
      prepared.PublishStatus === 'ToPublish' || prepared.PublishStatus === 'Published';

    const errors = validateAdvertise(prepared, getScreenSummary, {
      allowInactive: !isPublishing,
      requirePublishTargets: isPublishing,
    });

    if (errors.length) {
      NotificationManager.error(errors[0], 'Błąd', 2500);
      return;
    }

    setIsSaving(true);

    try {
      const formData = buildAdvertiseFormData(prepared);

      if (editorMode === 'edit' && prepared.AdvertiseId) {
        const res = await apiRequest(`advertise/${prepared.AdvertiseId}`, {
          method: 'PUT',
          data: formData,
        });

        NotificationManager.success(
          res?.message || 'Zestaw reklam zaktualizowany. Status cofnięty do: Nieopublikowane',
          'Sukces',
          2000
        );
      } else {
        const res = await apiRequest('advertise', {
          method: 'POST',
          data: formData,
        });

        NotificationManager.success(res?.message || 'Zestaw reklam dodany', 'Sukces', 2000);
      }

      closeEditor();
      await getAdvertises(filter, settingsFilter?.rowsPerPage, filter.page);
    } catch (err) {
      NotificationManager.error(getErrorMessage(err), 'Błąd', 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActiveAdvertise = async () => {
    if (!editor?.AdvertiseId) return;

    const isCurrentlyActive = toBool(editor.IsActive);
    const nextIsActive = !isCurrentlyActive;

    const confirmMessage = isCurrentlyActive
      ? 'Na pewno dezaktywować ten zestaw reklam?'
      : 'Na pewno aktywować ten zestaw reklam?';

    if (!window.confirm(confirmMessage)) return;

    const nextAdvertise = prepareDraftForSubmit(
      {
        ...editor,
        IsActive: nextIsActive,
      },
      getScreenSummary,
      getTerminalById
    );

    setIsSaving(true);

    try {
      const formData = buildAdvertiseFormData(nextAdvertise);

      const res = await apiRequest(`advertise/${editor.AdvertiseId}`, {
        method: 'PUT',
        data: formData,
      });

      NotificationManager.success(
        res?.message ||
          (nextIsActive ? 'Zestaw reklam aktywowany' : 'Zestaw reklam zdezaktywowany'),
        'Sukces',
        2000
      );

      closeEditor();
      await getAdvertises(filter, settingsFilter?.rowsPerPage, filter.page);
    } catch (err) {
      NotificationManager.error(getErrorMessage(err), 'Błąd', 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const updateStatus = async (item, nextStatus) => {
    try {
      const current = await getAdvertiseDetails(item.AdvertiseId);

      const nextAdvertise = prepareDraftForSubmit(
        {
          ...current,
          PublishStatus: nextStatus,
        },
        getScreenSummary,
        getTerminalById
      );

      const isPublishing = nextStatus === 'ToPublish' || nextStatus === 'Published';

      if (isPublishing) {
        const errors = validateAdvertise(nextAdvertise, getScreenSummary, {
          allowInactive: false,
          requirePublishTargets: true,
        });

        if (errors.length) {
          NotificationManager.error(errors[0], 'Błąd', 3000);
          return;
        }
      }

      const formData = buildAdvertiseFormData(nextAdvertise);

      const saveRes = await apiRequest(`advertise/${item.AdvertiseId}`, {
        method: 'PUT',
        data: formData,
      });

      const label = STATUS_META[nextStatus]?.label || nextStatus;

      NotificationManager.success(
        saveRes?.message || `Status zmieniony na: ${label}`,
        'Sukces',
        2000
      );

      await getAdvertises(filter, settingsFilter?.rowsPerPage, filter.page);
    } catch (err) {
      NotificationManager.error(getErrorMessage(err), 'Błąd', 3000);
    }
  };

  return (
    <div className="container-fluid p-0 filter-icon-fix">
      <SearchFilter
        filterName="advertises"
        defaultFilter={defaultFilter}
        reportList={reportList}
        onFilterChange={(f) => setFilter((prev) => ({ ...prev, ...f }))}
        onSettingsChange={(s) => setSettingsFilter((prev) => ({ ...prev, ...s }))}
        search={getAdvertises}
        totalCount={advertises.length}
        dataList={advertises}
        forNow
      />

      <div className="border">
        <div className="custom-scroll overflow-auto" style={{ maxHeight: 'calc(100vh - 170px)' }}>
          <table className="table table-striped">
            <thead className="headers-stick-machines">
              <tr className="text-center headers-color">
                <th style={{ cursor: 'pointer', userSelect: 'none', minWidth: 140 }}>
                  <div onClick={() => handleHeaderSort('Name')}>Nazwa{arrow('Name')}</div>
                  <div onClick={() => handleHeaderSort('Comment')}>
                    Komentarz{arrow('Comment')}
                  </div>
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }}
                  onClick={() => handleHeaderSort('PackageType')}
                >
                  Typ{arrow('PackageType')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }}
                  onClick={() => handleHeaderSort('FilesCount')}
                >
                  Pliki{arrow('FilesCount')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }}
                  onClick={() => handleHeaderSort('ScreenRatioKey')}
                >
                  Ekran{arrow('ScreenRatioKey')}
                  <br />
                  Rozdzielczość
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }}
                  onClick={() => handleHeaderSort('TerminalsCount')}
                >
                  Terminale{arrow('TerminalsCount')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }}
                  onClick={() => handleHeaderSort('StartDelaySec')}
                >
                  Bezczynność{arrow('StartDelaySec')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }}
                  onClick={() => handleHeaderSort('ImageDurationSec')}
                >
                  Wyświetlanie{arrow('ImageDurationSec')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 90 }}
                  onClick={() => handleHeaderSort('Priority')}
                >
                  Priorytet{arrow('Priority')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }}
                  onClick={() => handleHeaderSort('PublishStatus')}
                >
                  Status{arrow('PublishStatus')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 90 }}
                  onClick={() => handleHeaderSort('IsActive')}
                >
                  Aktywny{arrow('IsActive')}
                </th>

                <th
                  style={{ cursor: 'pointer', userSelect: 'none', minWidth: 110 }}
                  onClick={() => handleHeaderSort('UpdateDateTime')}
                >
                  Aktualizacja{arrow('UpdateDateTime')}
                </th>

                <th style={{ minWidth: 170 }}>
                  <div className="d-flex flex-column align-items-center">
                    <i className="btn btn-outline-secondary fas fa-plus" onClick={openNew} />
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {advertises.map((item) => {
                const type = item.PackageType || getAdvertiseType(item.FilesMeta);
                const screen = getScreenSummary(item.TerminalIds);
                const terminals = (item.TerminalIds || []).map(getTerminalById).filter(Boolean);
                const filesCount = Number.isFinite(Number(item.FilesCount))
                  ? Number(item.FilesCount)
                  : (item.FilesMeta || []).length;

                return (
                  <tr key={item.AdvertiseId}>
                    <td className="text-center">
                      <div className="font-weight-bold">{item.Name}</div>
                      <div className="text-muted small" style={{ whiteSpace: 'pre-wrap' }}>
                        {item.Comment || '-'}
                      </div>
                    </td>

                    <td className="text-center">
                      <span className={`badge ${type === 'Video' ? 'badge-primary' : 'badge-info'}`}>
                        {type}
                      </span>
                    </td>

                    <td className="text-center">
                      {filesCount ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <span className="font-weight-bold">{filesCount}</span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>

                    <td className="text-center">
                      <div
                        className={
                          screen.isMixed ? 'text-danger font-weight-bold' : 'font-weight-bold'
                        }
                      >
                        {screen.label}
                      </div>
                      <div className="text-muted small">{screen.resolution}</div>
                    </td>

                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center">
                        <span className="font-weight-bold">{terminals.length}</span>
                      </div>
                    </td>

                    <td className="text-center">{item.StartDelaySec}s</td>

                    <td className="text-center">{item.ImageDurationSec}s</td>

                    <td className="text-center">{item.Priority}</td>

                    <td className="text-center">{getStatusBadge(item.PublishStatus)}</td>

                    <td className="text-center">
                      <i
                        className={
                          item.IsActive
                            ? 'fas fa-check-square text-primary'
                            : 'far fa-square text-primary'
                        }
                      />
                    </td>

                    <td className="text-center">{formatDate(item.UpdateDateTime)}</td>

                    <td>
                      <div className="d-flex flex-wrap justify-content-center" style={{ gap: 6 }}>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => openEdit(item)}
                        >
                          Edytuj
                        </button>

                        {item.PublishStatus === 'NotPublished' && (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => updateStatus(item, 'ToPublish')}
                          >
                            Przekaż do publikacji
                          </button>
                        )}

                        {item.PublishStatus === 'ToPublish' && (
                          <>
                            {isConsole && (
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={() => updateStatus(item, 'Published')}
                              >
                                Opublikuj
                              </button>
                            )}

                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => updateStatus(item, 'NotPublished')}
                            >
                              Cofnij
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {advertises.length === 0 && <NoResults />}
        </div>
      </div>

      {editor && (
        <AdvertiseModal
          draft={editor}
          setDraft={setEditor}
          onClose={closeEditor}
          onSave={saveAdvertise}
          onToggleActive={toggleActiveAdvertise}
          mode={editorMode}
          isSaving={isSaving}
          terminalsCatalog={availableTerminals}
          getTerminalById={getTerminalById}
          getScreenSummary={getScreenSummary}
          clientId={clientId}
        />
      )}
    </div>
  );
}